import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import {
  MdArrowForward,
  MdCheckCircle,
  MdFavorite,
  MdPeople,
  MdVolunteerActivism,
} from "react-icons/md";
import AtriumApi from "api";
import Badge from "components/badge";
import Button from "components/button";
import Card, { CardHeader } from "components/card";
import DataTable from "components/data-table";
import { InputField, Select } from "components/fields";
import Loader from "components/loader/Loader";
import { PageContainer, PageHeader } from "components/page";
import StatCard, { TrendIndicator } from "components/stat-card";
import { EmptyState } from "components/states";
import { formatDate, formatNumber } from "utils/format";
import Weather from "./weather";

const DATE_RANGE_OPTIONS = [
  { value: "week", label: "This Week" },
  { value: "month", label: "This Month" },
  { value: "quarter", label: "This Quarter" },
  { value: "year", label: "This Year" },
  { value: "custom", label: "Custom Range" },
];

const DATE_RANGE_HINTS = {
  week: "Last 7 days",
  month: "Last 30 days",
  quarter: "Last 90 days",
  year: "Last 365 days",
};

const RESPONSE_STATUS = {
  urgent: { tone: "danger", label: "Needs Care" },
  followup: { tone: "warning", label: "Follow-up" },
  good: { tone: "success", label: "Healthy" },
};

const NO_ROWS = [];

const QUICK_ACTIONS = [
  { label: "Record Attendance", to: "/admin/attendance" },
  { label: "Send Heartbeat", to: "/admin/heartbeat" },
  { label: "View Reports", to: "/admin/attendance/reports" },
  { label: "Settings", to: "/admin/settings" },
];

const scoreFill = (score) => {
  if (score >= 3) return "bg-success";
  if (score >= 2) return "bg-warning";
  return "bg-danger";
};

/** Health score (0–5) as a small progress bar with the value beside it. */
const HealthScoreBar = ({ score }) => {
  if (score === null || score === undefined) return null;

  return (
    <div className="flex items-center gap-2">
      <div aria-hidden="true" className="h-1.5 flex-1 overflow-hidden rounded-full bg-surface-subtle">
        <div
          className={`h-full rounded-full transition-all ${scoreFill(score)}`}
          style={{ width: `${(score / 5) * 100}%` }}
        />
      </div>
      <span className="w-8 text-xs font-medium text-fg-muted">
        <span className="sr-only">Health score </span>
        {score.toFixed(1)}
      </span>
    </div>
  );
};

const HeartbeatResponseItem = ({ response, to }) => {
  const status = RESPONSE_STATUS[response.status] || RESPONSE_STATUS.good;
  const insights =
    response.insights?.length > 0 && response.insights[0] !== "No immediate needs"
      ? response.insights
      : null;

  return (
    <Link
      to={to}
      className="focus-ring block rounded-control border border-line p-3 transition-colors hover:bg-surface-subtle"
    >
      <div className="flex items-start gap-3">
        {response.avatar ? (
          <img src={response.avatar} alt="" className="h-10 w-10 shrink-0 rounded-full object-cover" />
        ) : (
          <span
            aria-hidden="true"
            className={`flex h-10 w-10 shrink-0 items-center justify-center rounded-full text-sm font-semibold ${
              response.isAnonymous
                ? "bg-surface-subtle text-fg-muted"
                : "bg-brand-100 text-brand-800 dark:bg-brand-900/40 dark:text-brand-200"
            }`}
          >
            {response.initials}
          </span>
        )}

        <div className="min-w-0 flex-1">
          <div className="mb-1 flex items-center justify-between gap-2">
            <div className="flex min-w-0 items-center gap-2">
              <p className="truncate text-sm font-semibold text-fg">{response.displayName}</p>
              <Badge tone={status.tone} size="sm">
                {status.label}
              </Badge>
            </div>
            <span className="shrink-0 text-xs text-fg-muted">{response.time}</span>
          </div>

          <div className="mb-2 flex items-center gap-3">
            <span className="truncate text-xs text-fg-muted">{response.campaignName}</span>
            {response.healthScore && (
              <div className="w-20 shrink-0">
                <HealthScoreBar score={response.healthScore} />
              </div>
            )}
          </div>

          {insights && (
            <div className="flex flex-wrap items-center gap-1">
              {insights.slice(0, 2).map((insight, idx) => (
                <Badge key={idx} size="sm">
                  {insight}
                </Badge>
              ))}
              {insights.length > 2 && (
                <span className="text-2xs text-fg-muted">+{insights.length - 2} more</span>
              )}
            </div>
          )}
        </div>
      </div>
    </Link>
  );
};

/** Small metric well inside a card. */
const Metric = ({ label, value, valueClassName = "text-fg", extra }) => (
  <div className="rounded-control bg-surface-raised p-3">
    <p className="text-xs text-fg-muted">{label}</p>
    <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
      <p className={`text-xl font-bold ${valueClassName}`}>{value}</p>
      {extra}
    </div>
  </div>
);

/** Row of three compact figures separated by dividers. */
const InlineStats = ({ items }) => (
  <dl className="grid grid-cols-3 divide-x divide-line text-center">
    {items.map(({ label, value }) => (
      <div key={label} className="flex flex-col-reverse px-2">
        <dt className="text-xs text-fg-muted">{label}</dt>
        <dd className="text-sm font-semibold text-fg">{value}</dd>
      </div>
    ))}
  </dl>
);

const IntegrationRow = ({ name, status }) => (
  <div className="flex items-start gap-3 rounded-control border border-line p-3">
    <MdCheckCircle aria-hidden="true" className="mt-0.5 h-5 w-5 shrink-0 text-success-fg" />
    <div className="flex-1">
      <p className="text-sm font-semibold text-fg">{name}</p>
      <p className="mt-1 text-xs text-fg-muted">{status}</p>
    </div>
    <Button as={Link} to="/admin/settings?tab=integrations" variant="ghost" size="sm">
      Manage
    </Button>
  </div>
);

const Section = ({ id, title, actions, children }) => (
  <section aria-labelledby={id} className="flex flex-col gap-3">
    <div className="flex flex-wrap items-center justify-between gap-2">
      <h2 id={id} className="text-heading">
        {title}
      </h2>
      {actions}
    </div>
    {children}
  </section>
);

const FunctionalDashboard = () => {
  const [selectedCampus, setSelectedCampus] = useState("all");
  const [dateRange, setDateRange] = useState("week");
  const [customStartDate, setCustomStartDate] = useState(
    new Date(Date.now() - 7 * 24 * 60 * 60 * 1000).toISOString().split('T')[0]
  );
  const [customEndDate, setCustomEndDate] = useState(
    new Date().toISOString().split('T')[0]
  );
  const [loading, setLoading] = useState(true);
  const [campuses, setCampuses] = useState([]);

  // Data states
  const [attendanceData, setAttendanceData] = useState(null);
  const [heartbeatData, setHeartbeatData] = useState(null);
  const [dashboardData, setDashboardData] = useState(null);
  const [demographics, setDemographics] = useState(null);
  const [integrationStatus, setIntegrationStatus] = useState({
    planningCenter: null,
    churchMetrics: null
  });

  // Fetch campuses
  useEffect(() => {
    const fetchCampuses = async () => {
      try {
        const response = await AtriumApi.get("/v2/campuses/allCampuses");
        if (response) {
          setCampuses(response?.data?.data?.campuses?.data || []);
        }
      } catch (error) {
        console.error("Error fetching campuses:", error);
      }
    };
    fetchCampuses();
  }, []);

  // Helper function to build date query parameters
  const buildDateQuery = () => {
    if (dateRange === "custom") {
      return `startDate=${customStartDate}&endDate=${customEndDate}`;
    }
    // For preset ranges, just pass the range type
    return `range=${dateRange}`;
  };

  // Fetch integration statuses separately
  useEffect(() => {
    const fetchIntegrationStatus = async () => {
      try {
        const pcStatus = await AtriumApi.get("/v1/connect");
        setIntegrationStatus(prev => ({
          ...prev,
          planningCenter: pcStatus?.data
        }));
      } catch (error) {
        console.log("Planning Center status error:", error);
      }

      try {
        const cmStatus = await AtriumApi.get("/v1/church-metrics/status");
        setIntegrationStatus(prev => ({
          ...prev,
          churchMetrics: cmStatus?.data
        }));
      } catch (error) {
        console.log("Church Metrics status error:", error);
      }
    };

    fetchIntegrationStatus();
  }, []);

  // Fetch all dashboard data
  useEffect(() => {
    const fetchDashboardData = async () => {
      setLoading(true);
      try {
        // Build query strings
        const dateQuery = buildDateQuery();
        const campusQuery = selectedCampus !== "all" ? `campusId=${selectedCampus}` : "";
        const fullQuery = [campusQuery, dateQuery].filter(Boolean).join("&");

        // Fetch attendance dashboard metrics with date range
        const attendanceResponse = await AtriumApi.get(
          `/v2/attendance-dashboard-metrics${fullQuery ? `?${fullQuery}` : ""}`
        );
        setAttendanceData(attendanceResponse?.data);

        // Fetch dashboard overview data
        const dashboardResponse = await AtriumApi.get(
          `/v1/dashboard${fullQuery ? `?${fullQuery}` : ""}`
        );
        setDashboardData(dashboardResponse?.data?.data);

        // Fetch heartbeat data (if available)
        try {
          const heartbeatResponse = await AtriumApi.get("/v2/heartbeat/campaigns/overview");
          setHeartbeatData(heartbeatResponse?.data?.data);
        } catch (error) {
          console.log("Heartbeat data not available:", error.message);
        }

        // Fetch demographics data from Data Axle
        try {
          const demographicsResponse = await AtriumApi.get("/v2/data-axle/demographics");
          setDemographics(demographicsResponse?.data?.data);
        } catch (error) {
          console.log("Demographics data not available:", error.message);
        }

      } catch (error) {
        console.error("Error fetching dashboard data:", error);
      } finally {
        setLoading(false);
      }
    };

    fetchDashboardData();
  }, [selectedCampus, dateRange, customStartDate, customEndDate]);

  // Calculate hero metrics
  const calculateHeroMetrics = () => {
    if (!attendanceData && !dashboardData) return null;

    // Attendance from dashboard metrics (last 30 days)
    const totalAttendance = attendanceData?.totals?.attendance || dashboardData?.totalVisitors || 0;
    const attendanceTrend = attendanceData?.trends?.attendance || 0;

    // First-time visitors from attendance metrics (last 30 days)
    const firstTimeVisitors = attendanceData?.totals?.firstTimeGuests || 0;

    // Salvations (from attendance metrics - last 30 days)
    const salvationsThisMonth = attendanceData?.totals?.salvations || 0;

    // Active connections (from heartbeat)
    const activeConnections = heartbeatData?.totalActiveMembers || 0;
    const responseRate = heartbeatData?.responseRate || 0;

    return {
      totalAttendance,
      attendanceTrend,
      firstTimeVisitors,
      salvationsThisMonth,
      activeConnections,
      responseRate
    };
  };

  const heroMetrics = calculateHeroMetrics();

  // Calculate demographics insights from Data Axle data
  const calculateDemographics = () => {
    if (!demographics) {
      return {
        mostRepresentedAge: "N/A",
        agePercentage: "0%",
        newMovers: "0%",
        volunteerParticipation: "0%",
        medianIncome: "N/A"
      };
    }

    // Find most represented age group
    const ageDistribution = demographics.ageDistribution || {};
    let maxAge = { range: "N/A", count: 0, percentage: "0%" };
    let totalWithAge = 0;

    Object.entries(ageDistribution).forEach(([range, count]) => {
      totalWithAge += count;
      if (count > maxAge.count) {
        maxAge = { range, count, percentage: "0%" };
      }
    });

    // Calculate percentage of most represented age
    if (totalWithAge > 0 && maxAge.count > 0) {
      maxAge.percentage = Math.round((maxAge.count / totalWithAge) * 100) + "%";
    }

    // Calculate median income from financial data
    const medianIncome = demographics.financial?.medianIncome
      ? `$${(demographics.financial.medianIncome / 1000).toFixed(0)}k`
      : "N/A";

    // Use actual volunteer participation from attendance data if available
    const volunteerParticipation = attendanceData?.totals?.volunteers
      ? Math.round((attendanceData.totals.volunteers / attendanceData.totals.attendance) * 100) + "%"
      : "0%";

    return {
      mostRepresentedAge: maxAge.range,
      agePercentage: maxAge.percentage,
      newMovers: "Data unavailable", // Would need new movers data from Data Axle
      volunteerParticipation: volunteerParticipation,
      medianIncome: medianIncome
    };
  };

  const demographicsInsights = calculateDemographics();

  const campusOptions = useMemo(
    () => [
      { value: "all", label: "All Campuses" },
      ...campuses.map((campus) => ({ value: campus.id, label: campus.name })),
    ],
    [campuses]
  );

  const breakdownColumns = useMemo(
    () => [
      {
        accessorKey: "category",
        header: "Category",
        cell: (c) => <span className="font-medium text-fg">{c.getValue()}</span>,
      },
      {
        accessorKey: "count",
        header: "Count",
        meta: { align: "center" },
        cell: (c) => <span className="text-lg font-bold text-fg">{formatNumber(c.getValue())}</span>,
      },
      {
        accessorKey: "percentage",
        header: "% of Total",
        meta: { align: "center" },
        cell: (c) => `${c.getValue()}%`,
      },
      {
        accessorKey: "trend",
        header: "vs Last Week",
        meta: { align: "center" },
        cell: (c) => <TrendIndicator value={c.getValue()} />,
      },
    ],
    []
  );

  const campusHint =
    selectedCampus === "all"
      ? `${campuses.length} campus${campuses.length !== 1 ? 'es' : ''}`
      : campuses.find(c => c.id === selectedCampus)?.name || '';

  const dateRangeHint =
    dateRange === "custom"
      ? `${formatDate(customStartDate)} to ${formatDate(customEndDate)}`
      : DATE_RANGE_HINTS[dateRange];

  const customRangeDays = Math.ceil(
    (new Date(customEndDate) - new Date(customStartDate)) / (1000 * 60 * 60 * 24)
  );

  const hasIntegration =
    integrationStatus.planningCenter?.isAccessTokenSet || integrationStatus.churchMetrics?.connected;

  return (
    <PageContainer>
      <PageHeader title="Test Dashboard" />

      <div className="grid grid-cols-1 gap-5 lg:grid-cols-3">
        <div className="lg:col-span-2">
          <Weather />
        </div>
        <Card padding="md" className="gap-4">
          <CardHeader title="Dashboard Filters" />
          <Select
            label="Campus Location"
            value={selectedCampus}
            onChange={(e) => setSelectedCampus(e.target.value)}
            options={campusOptions}
            hint={campusHint}
          />
          <Select
            label="Date Range"
            value={dateRange}
            onChange={(e) => setDateRange(e.target.value)}
            options={DATE_RANGE_OPTIONS}
            hint={dateRangeHint}
          />
          {dateRange === "custom" && (
            <div className="flex flex-col gap-2">
              <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-1 xl:grid-cols-2">
                <InputField
                  type="date"
                  label="Start Date"
                  value={customStartDate}
                  onChange={(e) => setCustomStartDate(e.target.value)}
                  max={customEndDate}
                />
                <InputField
                  type="date"
                  label="End Date"
                  value={customEndDate}
                  onChange={(e) => setCustomEndDate(e.target.value)}
                  min={customStartDate}
                  max={new Date().toISOString().split('T')[0]}
                />
              </div>
              <p className="text-xs text-fg-muted">{customRangeDays} days selected</p>
            </div>
          )}
        </Card>
      </div>

      <Section id="health-overview-heading" title="Church Health Overview">
        <div className="grid grid-cols-1 gap-5 md:grid-cols-2 xl:grid-cols-4">
          <StatCard
            size="lg"
            icon={MdPeople}
            accent="sage"
            label="Total Attendance (30 Days)"
            value={formatNumber(heroMetrics?.totalAttendance || 0)}
            trend={heroMetrics?.attendanceTrend ? { value: heroMetrics.attendanceTrend } : undefined}
            caption="vs previous 30 days"
            loading={loading}
          />
          <StatCard
            size="lg"
            icon={MdFavorite}
            accent="gold"
            label="First-Time Visitors"
            value={formatNumber(heroMetrics?.firstTimeVisitors || 0)}
            caption="This week"
            loading={loading}
          />
          <StatCard
            size="lg"
            icon={MdCheckCircle}
            accent="sky"
            label="Salvations & Decisions"
            value={formatNumber(heroMetrics?.salvationsThisMonth || 0)}
            caption="This month"
            loading={loading}
          />
          <StatCard
            size="lg"
            icon={MdVolunteerActivism}
            accent="slate"
            label="Active Connections"
            value={formatNumber(heroMetrics?.activeConnections || 0)}
            caption={`Response rate: ${heroMetrics?.responseRate || 0}%`}
            loading={loading}
          />
        </div>
      </Section>

      {loading ? (
        <Loader label="Loading dashboard" />
      ) : (
        <>
          <Section id="attendance-insights-heading" title="Attendance Insights">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <div className="flex flex-col gap-3">
                <h3 className="text-heading">Attendance Breakdown</h3>
                <DataTable
                  aria-label="Attendance breakdown"
                  columns={breakdownColumns}
                  data={attendanceData?.breakdown || NO_ROWS}
                  emptyState={<EmptyState variant="inline" title="No attendance data available" />}
                />
              </div>

              <Card padding="md" className="gap-4">
                <CardHeader title="Service Performance" titleAs="h3" />
                <EmptyState variant="inline" title="Service-specific data coming soon" />
              </Card>
            </div>
          </Section>

          <Section id="connection-care-heading" title="Connection & Care">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <Card padding="md" className="gap-4">
                <CardHeader
                  title="Heartbeat Engagement"
                  titleAs="h3"
                  actions={
                    <Button as={Link} to="/admin/heartbeat" variant="ghost" size="sm">
                      Manage Campaigns
                    </Button>
                  }
                />
                {heartbeatData ? (
                  <div className="flex flex-col gap-3">
                    <div className="grid grid-cols-3 gap-2">
                      <Metric label="Surveys (7d)" value={heartbeatData.surveysThisWeek || 0} />
                      <Metric label="Response Rate" value={`${heartbeatData.responseRate || 0}%`} />
                      <Metric
                        label="Needs Care"
                        value={heartbeatData.needsCare || 0}
                        valueClassName={heartbeatData.needsCare > 0 ? "text-danger-fg" : "text-fg"}
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <Metric
                        label="QR Scans (7d)"
                        value={heartbeatData.qrScansThisWeek || 0}
                        extra={
                          heartbeatData.qrConversionRate > 0 && (
                            <span className="text-xs text-fg-muted">{heartbeatData.qrConversionRate}% conv.</span>
                          )
                        }
                      />
                      <Metric
                        label="Prayer Requests"
                        value={heartbeatData.pendingPrayerRequests || 0}
                        extra={
                          heartbeatData.prayerRequestsThisWeek > 0 && (
                            <Badge tone="info" size="sm">+{heartbeatData.prayerRequestsThisWeek} new</Badge>
                          )
                        }
                      />
                    </div>

                    <div className="rounded-control border border-line p-3">
                      <p className="mb-2 text-xs font-medium text-fg-muted">Delivery Stats (7 days)</p>
                      <InlineStats
                        items={[
                          { label: "Emails", value: heartbeatData.emailsSentThisWeek || 0 },
                          { label: "SMS", value: heartbeatData.smsSentThisWeek || 0 },
                          { label: "Open Rate", value: `${heartbeatData.emailOpenRate || 0}%` },
                        ]}
                      />
                    </div>

                    <div className="rounded-control bg-surface-raised px-3 py-2">
                      <InlineStats
                        items={[
                          { label: "Want to Connect", value: heartbeatData.wantsToConnect || 0 },
                          { label: "Follow-up Requests", value: heartbeatData.followUpRequests || 0 },
                          { label: "Pending Actions", value: heartbeatData.pendingFollowUps || 0 },
                        ]}
                      />
                    </div>

                    <Button as={Link} to="/admin/heartbeat/action-items" variant="primary" fullWidth>
                      View All Care Needs
                    </Button>
                  </div>
                ) : (
                  <EmptyState
                    icon={MdFavorite}
                    title="No Heartbeat Data Available"
                    action={
                      <Button as={Link} to="/admin/heartbeat/new" variant="secondary">
                        Create Your First Campaign
                      </Button>
                    }
                  />
                )}
              </Card>

              <Card padding="md" className="gap-4">
                <CardHeader
                  title="Recent Heartbeat Responses"
                  titleAs="h3"
                  actions={
                    <Button as={Link} to="/admin/heartbeat" variant="ghost" size="sm">
                      View All
                    </Button>
                  }
                />
                {heartbeatData?.recentResponses && heartbeatData.recentResponses.length > 0 ? (
                  <div className="flex max-h-[340px] flex-col gap-2 overflow-y-auto pr-1">
                    {heartbeatData.recentResponses.map((response, idx) => (
                      <HeartbeatResponseItem
                        key={response.id || idx}
                        response={response}
                        to={`/admin/heartbeat/${response.campaignId}?responseId=${response.responseId}`}
                      />
                    ))}
                  </div>
                ) : (
                  <EmptyState
                    icon={MdFavorite}
                    title="No Responses Yet"
                    description="Responses will appear here as members complete surveys"
                  />
                )}
              </Card>
            </div>
          </Section>

          <Section
            id="growth-insights-heading"
            title="Growth Insights"
            actions={
              <Button
                as={Link}
                to="/admin/insights"
                variant="secondary"
                size="sm"
                rightIcon={<MdArrowForward aria-hidden="true" />}
              >
                View Full Insights
              </Button>
            }
          >
            <div className="grid grid-cols-1 gap-5 md:grid-cols-2 lg:grid-cols-4">
              <StatCard
                size="lg"
                label="Most Represented Age"
                value={demographicsInsights.mostRepresentedAge}
                caption={`${demographicsInsights.agePercentage} of congregation`}
              />
              <StatCard
                size="lg"
                label="New Movers (12mo)"
                value={demographicsInsights.newMovers}
                caption="Data unavailable"
              />
              <StatCard
                size="lg"
                label="Volunteer Participation"
                value={demographicsInsights.volunteerParticipation}
                caption="Active volunteers"
              />
              <StatCard
                size="lg"
                label="Median HH Income"
                value={demographicsInsights.medianIncome}
                caption="Household median"
              />
            </div>
          </Section>

          <Section id="system-status-heading" title="System Status">
            <div className="grid grid-cols-1 gap-5 lg:grid-cols-2">
              <Card padding="md" className="gap-4">
                <CardHeader title="Integration Status" titleAs="h3" />
                {hasIntegration ? (
                  <div className="flex flex-col gap-3">
                    {integrationStatus.planningCenter?.isAccessTokenSet && (
                      <IntegrationRow name="Planning Center" status="Connected" />
                    )}
                    {integrationStatus.churchMetrics?.connected && (
                      <IntegrationRow name="Church Metrics" status="Connected - Auto-sync enabled" />
                    )}
                  </div>
                ) : (
                  <EmptyState
                    variant="first-run"
                    title="No Integrations Connected"
                    action={
                      <Button
                        as={Link}
                        to="/admin/settings?tab=integrations"
                        variant="secondary"
                        rightIcon={<MdArrowForward aria-hidden="true" />}
                      >
                        Connect Integrations
                      </Button>
                    }
                  />
                )}
              </Card>

              <Card padding="md" className="gap-4">
                <CardHeader title="Quick Actions" titleAs="h3" />
                <nav aria-label="Quick actions" className="flex flex-col gap-3">
                  {QUICK_ACTIONS.map(({ label, to }) => (
                    <Link
                      key={to}
                      to={to}
                      className="focus-ring flex items-center justify-between rounded-control bg-surface-raised p-3 text-sm font-medium text-fg transition-colors hover:bg-surface-subtle"
                    >
                      {label}
                      <MdArrowForward aria-hidden="true" className="h-4 w-4 text-fg-muted" />
                    </Link>
                  ))}
                </nav>
              </Card>
            </div>
          </Section>
        </>
      )}
    </PageContainer>
  );
};

export default FunctionalDashboard;
